import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { BudgetDashboardWidget } from '../../types';
import { fetchBudgetDashboardWidget } from '../../api';
import { formatCurrency } from '../../modules/reports/components/ReportHelpers';

const ProgressBar: React.FC<{ actual: number; budget: number; type: 'income' | 'expense' }> = ({
  actual, budget, type,
}) => {
  const pct = budget > 0 ? Math.min((actual / budget) * 100, 100) : 0;
  const isWarning = type === 'expense' && pct >= 90;
  const isGood = type === 'income' && pct >= 90;

  return (
    <div className='progress h-8px mb-1' style={{ borderRadius: 4 }}>
      <div
        className={clsx(
          'progress-bar',
          isWarning ? 'bg-danger' : isGood ? 'bg-success' : type === 'income' ? 'bg-primary' : 'bg-warning',
        )}
        style={{ width: `${pct}%`, transition: 'width 0.5s ease' }}
      />
    </div>
  );
};

const BudgetTrackerWidget: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<BudgetDashboardWidget | null | 'loading'>('loading');

  useEffect(() => {
    fetchBudgetDashboardWidget()
      .then(d => setData(d))
      .catch(() => setData(null));
  }, []);

  if (data === 'loading') return null;

  if (!data) {
    return (
      <div className='card card-flush h-100'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title fw-bold text-dark'>{'Budget Tracker'.toTranslatedDisplay()}</h3>
        </div>
        <div className='card-body pt-0 pb-5 text-center text-muted'>
          <i className='bi bi-calculator fs-2 d-block mb-2' />
          {'No active budget for this financial year.'.toTranslatedDisplay()}{' '}
          <button
            className='btn btn-link p-0'
            onClick={() => navigate('/budgets/new')}
          >
            {'Create one →'.toTranslatedDisplay()}
          </button>
        </div>
      </div>
    );
  }

  const incomeYtdPct = data.totalBudgetIncome > 0
    ? Math.round((data.ytdActualIncome / data.totalBudgetIncome) * 1000) / 10
    : 0;
  const expenseYtdPct = data.totalBudgetExpense > 0
    ? Math.round((data.ytdActualExpense / data.totalBudgetExpense) * 1000) / 10
    : 0;
  const incomeRemaining = data.totalBudgetIncome - data.ytdActualIncome;
  const expenseRemaining = data.totalBudgetExpense - data.ytdActualExpense;
  const netVariancePct = data.netBudgetProfit !== 0
    ? Math.round(((data.netActualProfit - data.netBudgetProfit) / Math.abs(data.netBudgetProfit)) * 1000) / 10
    : 0;

  return (
    <div className='card card-flush h-100'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title fw-bold text-dark'>
          {'Budget Tracker'.toTranslatedDisplay()}
          <span className='text-muted fw-normal ms-2 fs-7'>
            {'FY'.toTranslatedDisplay()} {data.financialYear} · {data.budgetName}
          </span>
        </h3>
        <div className='card-toolbar gap-2'>
          <button
            className='btn btn-icon btn-sm btn-light-primary'
            title={'Edit budget'.toTranslatedDisplay()}
            onClick={() => navigate(`/budgets/${data.budgetId}`)}
          >
            <i className='bi bi-pencil fs-6' />
          </button>
          <button
            className='btn btn-sm btn-light-primary'
            onClick={() => navigate(`/budgets/vs-actual?budgetId=${data.budgetId}`)}
          >
            {'Full Report →'.toTranslatedDisplay()}
          </button>
        </div>
      </div>

      <div className='card-body pt-2 pb-4'>
        {/* Income */}
        <div className='mb-4'>
          <div className='d-flex justify-content-between mb-1'>
            <span className='fw-semibold text-gray-700'>{'Income'.toTranslatedDisplay()}</span>
            <span className='text-muted fs-7'>
              {formatCurrency(data.ytdActualIncome)} {'of'.toTranslatedDisplay()} {formatCurrency(data.totalBudgetIncome)}
            </span>
          </div>
          <ProgressBar actual={data.ytdActualIncome} budget={data.totalBudgetIncome} type='income' />
          <div className='d-flex justify-content-between'>
            <span className='text-muted fs-8'>{incomeYtdPct}% {'achieved'.toTranslatedDisplay()}</span>
            <span className='text-muted fs-8'>{formatCurrency(Math.abs(incomeRemaining))} {incomeRemaining >= 0 ? 'remaining'.toTranslatedDisplay() : 'over'.toTranslatedDisplay()}</span>
          </div>
        </div>

        {/* Expenses */}
        <div className='mb-4'>
          <div className='d-flex justify-content-between mb-1'>
            <span className='fw-semibold text-gray-700'>{'Expenses'.toTranslatedDisplay()}</span>
            <span className='text-muted fs-7'>
              {formatCurrency(data.ytdActualExpense)} {'of'.toTranslatedDisplay()} {formatCurrency(data.totalBudgetExpense)}
            </span>
          </div>
          <ProgressBar actual={data.ytdActualExpense} budget={data.totalBudgetExpense} type='expense' />
          <div className='d-flex justify-content-between'>
            <span className='text-muted fs-8'>{expenseYtdPct}% {'used'.toTranslatedDisplay()}</span>
            <span className={clsx('fs-8', expenseRemaining < 0 ? 'text-danger fw-semibold' : 'text-muted')}>
              {formatCurrency(Math.abs(expenseRemaining))} {expenseRemaining >= 0 ? 'remaining'.toTranslatedDisplay() : 'over budget'.toTranslatedDisplay()}
            </span>
          </div>
        </div>

        {/* Net Profit */}
        <div className='border-top pt-3 mb-3'>
          <div className='d-flex justify-content-between align-items-center'>
            <span className='fw-semibold text-gray-700'>{'Net Profit'.toTranslatedDisplay()}</span>
            <div className='text-end'>
              <div className='text-muted fs-8'>{'Budgeted:'.toTranslatedDisplay()} {formatCurrency(data.netBudgetProfit)}</div>
              <div className={clsx('fw-bold', data.netActualProfit >= 0 ? 'text-success' : 'text-danger')}>
                {'Actual:'.toTranslatedDisplay()} {formatCurrency(data.netActualProfit)}
                {netVariancePct !== 0 && (
                  <span className='ms-1 fs-8'>({netVariancePct > 0 ? '+' : ''}{netVariancePct}%)</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top overspent accounts */}
        {data.topOverspentAccounts.length > 0 && (
          <div className='border-top pt-3'>
            <div className='fw-semibold text-gray-700 mb-2 fs-7'>{'Top Overspent'.toTranslatedDisplay()}</div>
            {data.topOverspentAccounts.map(a => (
              <div key={a.accountId} className='d-flex justify-content-between align-items-center mb-1'>
                <span className='fs-8 text-gray-600'>{a.accountName}</span>
                <span className='text-danger fw-semibold fs-8'>
                  +{formatCurrency(a.overspend)} ({a.overspendPct}%)
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetTrackerWidget;
